import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import ProviderLayout from "@/layouts/ProviderLayout";
import { 
  BarChart as BarChartIcon, Download, TrendingUp, Users, Calendar,
  DollarSign, Activity, FileText, ChevronDown, PieChart as PieChartIcon 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/ui/list-pagination";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { useToast } from "@/hooks/use-toast";
import { useGetProviderAppointments, useGetGroupAppointments } from "@/lib/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { motion, Variants } from "framer-motion";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.35, ease: "easeOut" },
  }),
};

export default function ProviderReports() {
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [timeRange, setTimeRange] = useState("all");
  const [selectedProvider, setSelectedProvider] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const isClinic = user?.provider?.practiceType === "group" || localStorage.getItem("practiceType") === "group";

  // Fetch real data
  const { data: providerAppts } = useGetProviderAppointments({ limit: 1000 }, { query: { enabled: !!user && !isClinic } });
  const { data: groupAppts } = useGetGroupAppointments({ limit: 1000 }, { query: { enabled: !!user && isClinic } });
  
  const rawAppointments = (isClinic ? groupAppts?.items : providerAppts?.items) || [];

  // Filter by selected provider (if group)
  const appointments = useMemo(() => {
    if (!isClinic || selectedProvider === 'all') return rawAppointments;
    return rawAppointments.filter((a: any) => 
      a.providerId === selectedProvider || 
      (a.provider?.user?.id === selectedProvider)
    );
  }, [rawAppointments, isClinic, selectedProvider]);

  // Aggregate Revenue Data over time
  const revenueData = useMemo(() => {
    if (!appointments.length) {
       // Return empty skeleton so chart doesn't crash
       return ['Jan', 'Feb', 'Mar', 'Apr', 'May'].map(m => ({ name: m, revenue: 0, appointments: 0 }));
    }
    
    const monthMap: Record<string, { revenue: number, appointments: number }> = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Determine the range of months to show based on data
    appointments.forEach((apt: any) => {
      if (apt.status !== 'COMPLETED') return;
      const date = new Date(apt.createdAt);
      if (isNaN(date.getTime())) return;
      const month = months[date.getMonth()];
      if (!monthMap[month]) {
        monthMap[month] = { revenue: 0, appointments: 0 };
      }
      monthMap[month].revenue += Number(apt.amount || 0);
      monthMap[month].appointments += 1;
    });
    
    return Object.keys(monthMap).sort((a, b) => months.indexOf(a) - months.indexOf(b)).map(m => ({
      name: m,
      ...monthMap[m]
    }));
  }, [appointments]);

  // Aggregate Services Data
  const servicesData = useMemo(() => {
    const serviceMap: Record<string, number> = {};
    appointments.forEach((apt: any) => {
      if (apt.status !== 'COMPLETED') return;
      const serviceName = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');
      serviceMap[serviceName] = (serviceMap[serviceName] || 0) + 1;
    });
    return Object.entries(serviceMap).map(([name, value]) => ({ name, value }));
  }, [appointments]);

  // Group Practice Performance
  const groupPerformance = useMemo(() => {
    if (!isClinic) return [];
    const perfMap: Record<string, any> = {};
    rawAppointments.forEach((apt: any) => {
      if (apt.status !== 'COMPLETED') return;
      const userObj = apt.provider?.user;
      const provName = userObj ? (userObj.displayName || `${userObj.firstName} ${userObj.lastName}`) : 'Unassigned';
      const provId = userObj?.id || 'unassigned';
      
      if (!perfMap[provId]) {
         perfMap[provId] = { id: provId, name: provName, specialty: apt.provider?.specialties?.[0] || 'Provider', revenue: 0, appointments: 0, rating: 5.0 };
      }
      perfMap[provId].revenue += Number(apt.amount || 0);
      perfMap[provId].appointments += 1;
    });
    return Object.values(perfMap).sort((a: any, b: any) => b.revenue - a.revenue);
  }, [rawAppointments, isClinic]);

  // Paginated appointments for the table
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const totalCount = 0; // Default fallback
  
  const recentAppointments = [...appointments]
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice((page - 1) * itemsPerPage, page * itemsPerPage)
    .map((apt: any) => {
      const patientName = apt.member?.user?.displayName || apt.member?.user?.firstName ? `${apt.member.user.firstName} ${apt.member.user.lastName}` : 'Unknown Patient';
      
      const pType = (isClinic || apt.groupName) ? 'Group Practice' : 'Individual Practice';
      
      let subText = null;
      if (isClinic && apt.provider?.user) {
        subText = `Assigned: ${apt.provider.user.displayName || `${apt.provider.user.firstName} ${apt.provider.user.lastName}`}`;
      } else if (!isClinic && apt.groupName) {
        subText = `via ${apt.groupName}`;
      }

      return {
        id: apt.id,
        date: format(new Date(apt.createdAt), 'MMM d, yyyy'),
        patient: patientName,
        type: pType,
        subText: subText,
        service: apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'Consultation'),
        revenue: Number(apt.amount || 0).toFixed(2),
        status: apt.status
      };
    });

  const handleExportCSV = () => {
    let csv = "Month,Revenue,Appointments\\n";
    revenueData.forEach((row: any) => {
      csv += `${row.name},${row.revenue},${row.appointments}\\n`;
    });

    if (isClinic) {
      csv += "\\nProvider Performance\\n";
      csv += "Name,Specialty,Revenue,Appointments,Rating\\n";
      groupPerformance.forEach((p: any) => {
        csv += `"${p.name}","${p.specialty}",${p.revenue},${p.appointments},${p.rating}\\n`;
      });
    }

    csv += "\\nAppointment History\\n";
    csv += "Date,Provider,Patient,Service,Revenue,Status\\n";
    
    // Sort all appointments chronologically
    const exportAppointments = [...appointments].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    exportAppointments.forEach((apt: any) => {
      const date = format(new Date(apt.createdAt), 'MMM d, yyyy');
      const provider = apt.provider?.user?.displayName || apt.groupName || 'Group Provider';
      const patient = apt.member?.user?.displayName || apt.member?.user?.firstName ? `${apt.member.user.firstName} ${apt.member.user.lastName}` : 'Unknown Patient';
      const service = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');
      const revenue = Number(apt.amount || 0).toFixed(2);
      
      csv += `"${date}","${provider}","${patient}","${service}",${revenue},${apt.status}\\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `practice_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({ title: "Export Successful", description: "Your report has been downloaded as a CSV." });
  };

  const totalYtdRevenue = revenueData.reduce((sum: number, row: any) => sum + row.revenue, 0);
  const totalYtdAppointments = revenueData.reduce((sum: number, row: any) => sum + row.appointments, 0);

  return (
    <ProviderLayout>
      <div className="w-full pt-4 sm:pt-6 lg:pt-8 space-y-5 sm:space-y-6">
        {/* Top Header */}
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 bg-card p-4 sm:p-5 rounded-2xl border border-border shadow-xs"
        >
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <PieChartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Reports & Analytics
              </h1>
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
                Comprehensive performance insights based on your actual appointments.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
            {isClinic && (
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger className="w-[180px] sm:w-[200px] h-9 sm:h-10 rounded-xl bg-background border-border text-xs sm:text-sm font-medium shadow-2xs">
                  <SelectValue placeholder="Filter by Provider" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">All Providers (Group Total)</SelectItem>
                  {groupPerformance.map((p: any) => (
                    <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[130px] sm:w-[150px] h-9 sm:h-10 rounded-xl bg-background border-border text-xs sm:text-sm font-medium shadow-2xs">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={handleExportCSV} className="h-9 sm:h-10 px-4 rounded-xl gap-2 font-semibold text-xs sm:text-sm shadow-2xs bg-primary hover:bg-primary/90 text-primary-foreground">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          </div>
        </motion.div>

        {/* Top KPIs Banner */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs bg-card transition-all hover:shadow-sm hover:border-primary/40 h-full">
              <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
                <p className="text-[11px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider leading-none">
                  Total Revenue
                </p>
                <div className="flex items-center justify-between gap-3 my-2.5">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                    ${totalYtdRevenue.toLocaleString()}
                  </h3>
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-2xs">
                    <DollarSign className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs bg-card transition-all hover:shadow-sm hover:border-primary/40 h-full">
              <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
                <p className="text-[11px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider leading-none">
                  Total Appointments
                </p>
                <div className="flex items-center justify-between gap-3 my-2.5">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                    {totalYtdAppointments.toLocaleString()}
                  </h3>
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 shadow-2xs">
                    <Calendar className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs bg-card transition-all hover:shadow-sm hover:border-primary/40 h-full">
              <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
                <p className="text-[11px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider leading-none">
                  Unique Patients
                </p>
                <div className="flex items-center justify-between gap-3 my-2.5">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                    {new Set(appointments.map((a:any) => a.memberId)).size}
                  </h3>
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 shadow-2xs">
                    <Users className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs bg-card transition-all hover:shadow-sm hover:border-primary/40 h-full">
              <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
                <p className="text-[11px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider leading-none">
                  Average Revenue / Appt
                </p>
                <div className="flex items-center justify-between gap-3 my-2.5">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                    ${Math.round(totalYtdRevenue / (totalYtdAppointments || 1)).toLocaleString()}
                  </h3>
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 shadow-2xs">
                    <Activity className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-2">
            <Card className="rounded-2xl border-border shadow-xs h-full flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" /> Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col justify-center">
                <div className="w-full h-[320px]">
                  {revenueData.length > 0 && revenueData[0].revenue > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'currentColor', opacity: 0.5 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'currentColor', opacity: 0.5 }} tickFormatter={(v) => `$${v}`} />
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.1} />
                        <Tooltip 
                          formatter={(val: number) => [`$${val.toLocaleString()}`, "Revenue"]}
                          contentStyle={{ borderRadius: '12px', border: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        />
                        <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                      <Activity className="w-12 h-12 mb-4 opacity-20" />
                      <p>Not enough real appointment data to show trend.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div custom={6} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs h-full flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <BarChartIcon className="w-5 h-5 text-primary" /> Services Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col justify-center">
                <div className="w-full h-[320px]">
                  {servicesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={servicesData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={2} dataKey="value">
                          {servicesData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip 
                          formatter={(val: number) => [`${val} Appts`, "Count"]}
                          contentStyle={{ borderRadius: '12px', border: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '15px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No services recorded yet.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Group Practice Specific Report */}
        {isClinic && selectedProvider === 'all' && (
          <motion.div custom={7} variants={fadeUp} initial="hidden" animate="visible">
            <Card className="rounded-2xl border-border shadow-xs overflow-hidden">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-lg font-bold">Provider Performance</CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Breakdown of revenue and appointments by care team member.
                </CardDescription>
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="px-6 py-4 font-bold">Provider Name</TableHead>
                      <TableHead className="px-6 py-4 font-bold text-right">Appointments</TableHead>
                      <TableHead className="px-6 py-4 font-bold text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupPerformance.map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell className="px-6 py-4 font-bold">{p.name}</TableCell>
                        <TableCell className="px-6 py-4 text-right font-medium">{p.appointments.toLocaleString()}</TableCell>
                        <TableCell className="px-6 py-4 text-right font-bold text-primary">${p.revenue.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {groupPerformance.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="px-6 py-8 text-center text-muted-foreground">
                          No appointments recorded by group providers yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Appointment History Table */}
        <motion.div custom={8} variants={fadeUp} initial="hidden" animate="visible">
          <Card className="rounded-2xl border-border shadow-xs overflow-hidden">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" /> Appointment History
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Detailed breakdown of your past and upcoming patient visits.
              </CardDescription>
            </CardHeader>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="px-6 py-4 font-bold">Date</TableHead>
                    <TableHead className="px-6 py-4 font-bold">Patient</TableHead>
                    <TableHead className="px-6 py-4 font-bold">Service</TableHead>
                    <TableHead className="px-6 py-4 font-bold text-right">Revenue</TableHead>
                    <TableHead className="px-6 py-4 font-bold text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentAppointments.map((appt: any) => (
                    <TableRow key={appt.id}>
                      <TableCell className="px-6 py-4 font-medium">{appt.date}</TableCell>
                      <TableCell className="px-6 py-4">
                        <div className="font-bold text-foreground">{appt.patient}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {appt.type === 'Group Practice' ? (
                            appt.subText ? appt.subText : 'Group Practice'
                          ) : 'Individual Practice'}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-muted-foreground">{appt.service}</TableCell>
                      <TableCell className="px-6 py-4 text-right font-bold text-primary">${appt.revenue}</TableCell>
                      <TableCell className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full border font-bold text-xs ${
                          appt.status === 'PENDING' ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                          appt.status === 'APPROVED' ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' :
                          appt.status === 'REJECTED' ? 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800' :
                          appt.status === 'COMPLETED' ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                          'bg-muted text-muted-foreground border-border'
                        }`}>
                          {appt.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentAppointments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                        No appointments found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {totalPages > 0 && (
              <div className="p-4 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4 mt-auto flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-medium">Rows per page:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(val) => { setItemsPerPage(Number(val)); setPage(1); }}>
                    <SelectTrigger className="w-20 h-9 bg-card border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <ListPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </ProviderLayout>
  );
}
